import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { getAuthToken } from './auth';
import type { RewriteRequest, RewriteResponse, HistoryResponse } from '../types';

const API_URL = import.meta.env.VITE_API_URL;

export async function getTokenOrThrow(): Promise<string> {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');
  return token;
}

export async function authenticatedFetch(
  url: string,
  options: RequestInit,
  token: string
): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const newToken = session.tokens?.idToken?.toString();

      if (!newToken) throw new Error('No token after refresh');

      const retryResponse = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${newToken}`,
        },
      });

      if (retryResponse.status === 401) {
        await signOut();
        window.location.href = '/login';
      }

      return retryResponse;
    } catch {
      await signOut();
      window.location.href = '/login';
      throw new Error('Session expired');
    }
  }

  return response;
}

export async function rewrite(request: RewriteRequest): Promise<RewriteResponse> {
  const token = await getTokenOrThrow();
  const res = await authenticatedFetch(
    `${API_URL}/v1/improve`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
    token
  );
  if (res.status === 429) {
    const data = await res.json();
    throw Object.assign(new Error('Daily limit reached'), {
      code: 'LIMIT_REACHED',
      upgradeUrl: data.upgrade_url,
    });
  }
  if (!res.ok) throw new Error('Rewrite failed');
  return res.json();
}

export async function getHistory(
  limit = 20,
  cursor?: string
): Promise<HistoryResponse> {
  const token = await getTokenOrThrow();
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  const res = await authenticatedFetch(
    `${API_URL}/v1/history?${params}`,
    { headers: { 'Content-Type': 'application/json' } },
    token
  );
  if (res.status === 403) throw new Error('Pro subscription required');
  if (!res.ok) throw new Error('Failed to fetch history');
  return res.json();
}

export async function createCheckoutSession(): Promise<string> {
  const token = await getTokenOrThrow();
  const res = await authenticatedFetch(
    `${API_URL}/v1/payments/create-checkout-session`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
    token
  );
  if (!res.ok) throw new Error('Failed to create checkout session');
  const data = await res.json();
  return data.checkout_url;
}

export async function createPortalSession(): Promise<string> {
  const token = await getTokenOrThrow();
  const res = await authenticatedFetch(
    `${API_URL}/v1/payments/create-portal-session`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
    token
  );
  if (!res.ok) throw new Error('Failed to create portal session');
  const data = await res.json();
  return data.portal_url;
}
